import type { MailboxInfo } from "owa-client-types";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getUnifiedGroupsSettingsStore } from "../UnifiedGroupsSettingsStore";
import type { NamingPolicySettings, Classification, UnifiedGroupsSettingsState, } from "../schema/UnifiedGroupsSettingsState";
import { GroupAccessType } from "../schema/UnifiedGroupsSettingsState";

export function getUnifiedGroupsSettings(mailboxInfo: MailboxInfo): UnifiedGroupsSettingsState {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const groupsSettings = getUnifiedGroupsSettingsStore().unifiedGroupsSettingsByMailboxInfo.get(accountKey);
    
    if (!groupsSettings) {
        const defaultUnifiedGropusSettings: UnifiedGroupsSettingsState = {
            supportedClassifications: [],
            namingPolicySettings: {},
            groupsGuidelinesLink: "",
            orgAllowAddGuests: false,
            groupCreationEnabled: false,
            isSensitivityLabelsEnabled: false,
            IsGroupFoldersAndRulesEnabled: false,
            BlockMoveMessagesForGroupFolders: false,
            IsGroupMemberAllowedToEditContent: false,
            defaultClassification: "",
            defaultGroupAccessType: GroupAccessType.Private,
        };
        
        getUnifiedGroupsSettingsStore().unifiedGroupsSettingsByMailboxInfo.set(accountKey, defaultUnifiedGropusSettings);
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
         * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
         *	> Forbidden non-null assertion. */
        return getUnifiedGroupsSettingsStore().unifiedGroupsSettingsByMailboxInfo.get(accountKey)!;
    }
    
    return groupsSettings;
}

export function getDefaultGroupAccessType(mailboxInfo: MailboxInfo): GroupAccessType {
    return getUnifiedGroupsSettings(mailboxInfo).defaultGroupAccessType ?? GroupAccessType.Private;
}

export function getDefaultClassification(mailboxInfo: MailboxInfo): string {
    return getUnifiedGroupsSettings(mailboxInfo).defaultClassification ?? "";
}

export function getSupportedClassification(mailboxInfo: MailboxInfo): Classification[] {
    return getUnifiedGroupsSettings(mailboxInfo).supportedClassifications;
}

export function getNamingPolicySettings(mailboxInfo: MailboxInfo): NamingPolicySettings {
    return getUnifiedGroupsSettings(mailboxInfo).namingPolicySettings;
}

export function getGroupsGuidelinesLink(mailboxInfo: MailboxInfo): string {
    return getUnifiedGroupsSettings(mailboxInfo).groupsGuidelinesLink;
}

export function getOrgAlloAddGuests(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).orgAllowAddGuests;
}

export function getGroupCreationEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).groupCreationEnabled;
}

export function getIsSensitivityLabelsEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).isSensitivityLabelsEnabled;
}

export function getIsGroupFoldersAndRulesEnabled(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).IsGroupFoldersAndRulesEnabled;
}

export function getBlockMoveMessagesForGroupFolders(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).BlockMoveMessagesForGroupFolders;
}

export function getIsGroupMemberAllowedToEditContent(mailboxInfo: MailboxInfo): boolean | undefined {
    return getUnifiedGroupsSettings(mailboxInfo).IsGroupMemberAllowedToEditContent;
}
